import { useLocation } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import SeoHelmet from "@/components/UI/SeoHelmet";
import PageHero from "@/components/UI/PageHero";
import ActionLink from "@/components/UI/ActionLink";
import SupplierOfferForm from "@/components/Forms/SupplierOfferForm";
import SupplierRecommendationForm from "@/components/Forms/SupplierRecommendationForm";
import foodProduct from "@/public/svg/food-product.svg";
import plantHand from "@/public/svg/plant_hand.svg";
import pretzel from "@/public/svg/pretzel.svg";
import ecoHouse from "@/public/svg/eco_house.svg";
import candyJar from "@/public/svg/candy-jar.svg";
import users from "@/public/svg/users-2.svg";
import check from "@/public/svg/check.svg";
import seoImg from "@/public/img/seo/produzentenformular.png";

const supplierKinds = [
    { icon: pretzel, label: "Bäckereien" },
    { icon: ecoHouse, label: "Höfe" },
    { icon: candyJar, label: "Manufakturen" },
    { icon: users, label: "Weitere Anbieter" },
];

const steps = [
    { title: "Kontakt aufnehmen", text: "Stell uns deine Produkte vor oder schlage einen Anbieter vor." },
    { title: "Gemeinsam prüfen", text: "Wir besprechen Sortiment, Standorte und die mögliche Belieferung." },
    { title: "Zusammenarbeit planen", text: "Wenn es passt, stimmen wir die nächsten Schritte miteinander ab." },
];

const questions = [
    "Welche Produkte bietest du an?",
    "Welche Standorte kannst du beliefern?",
    "Wann und wie oft ist eine Lieferung möglich?",
];

const arrow = <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />;

export default function SuppliersPage() {
    const { hash } = useLocation();

    return (
        <>
            <SeoHelmet
                title="Yobsti für Lieferanten – Gutes aus der Region"
                description="Biete deine Produkte bei Yobsti an oder empfiehl uns einen regionalen Anbieter."
                image={seoImg}
                url="https://www.yobsti.de/fuer-lieferanten"
            />

            <PageHero
                eyebrow="Für Lieferanten"
                title={<>Gutes aus der Region.<br />Für deinen Yobsti.</>}
                text="Du stellst Lebensmittel her oder kennst einen regionalen Anbieter? Bring uns zusammen."
            >
                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="flex flex-col items-start gap-3 rounded-2xl bg-primaryLight/70 p-6">
                        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primaryLight">
                            <img src={foodProduct} alt="" className="h-10 w-10" />
                        </span>
                        <h2 className="text-2xl font-bold">Deine Produkte bei Yobsti</h2>
                        <p className="flex-1 text-lg">Zeig uns, was du herstellst und wohin du liefern kannst.</p>
                        <ActionLink to="#anbieten" variant="solid" event="cta-click" eventData={{ target: 'supplier-offer', location: 'suppliers-hero' }}>Eigene Produkte anbieten {arrow}</ActionLink>
                    </div>
                    <div className="flex flex-col items-start gap-3 rounded-2xl bg-secondaryLight p-6">
                        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary/40">
                            <img src={plantHand} alt="" className="h-10 w-10" />
                        </span>
                        <h2 className="text-2xl font-bold">Gute Anbieter aus deiner Nähe</h2>
                        <p className="flex-1 text-lg">Deine Lieblingsbäckerei oder dein Hofladen fehlt noch?</p>
                        <ActionLink to="#empfehlen" event="cta-click" eventData={{ target: 'supplier-recommendation', location: 'suppliers-hero' }}>Regionalen Anbieter empfehlen {arrow}</ActionLink>
                    </div>
                </div>
            </PageHero>

            {hash === "#empfehlen" && (
                <section id="empfehlen" className="max-w-3xl mx-auto px-4 pt-12 text-textColor scroll-mt-4">
                    <h2 className="text-2xl font-bold mb-2">Wen sollten wir aus deiner Region kennenlernen?</h2>
                    <p className="mb-6">
                        Verrate uns, wen wir ansprechen sollten und welche Produkte du dir in deinem Yobsti wünschst. Wir prüfen, ob eine Zusammenarbeit möglich ist. Nicht jede Empfehlung kann ins Sortiment aufgenommen werden.
                    </p>
                    <SupplierRecommendationForm />
                </section>
            )}

            <section className="max-w-6xl mx-auto px-4 py-12 text-textColor">
                <h2 className="text-3xl sm:text-4xl font-extrabold">Mehr Vielfalt aus deiner Region.</h2>
                <p className="mt-2 text-lg">Wir möchten unser Sortiment gemeinsam mit regionalen Betrieben erweitern.</p>
                <ul className="mt-8 grid grid-cols-2 gap-8 md:grid-cols-4">
                    {supplierKinds.map((kind) => (
                        <li key={kind.label} className="flex flex-col items-center gap-3 text-center">
                            <span className="flex h-24 w-24 items-center justify-center rounded-full bg-primaryLight">
                                <img src={kind.icon} alt="" className="h-12 w-12" />
                            </span>
                            <span className="text-lg font-semibold">{kind.label}</span>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="max-w-6xl mx-auto px-4 text-textColor">
                <div className="rounded-2xl bg-primaryLight/50 p-6 sm:p-10">
                    <h2 className="text-3xl sm:text-4xl font-extrabold mb-8">So kommen wir zusammen.</h2>
                    <ol className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {steps.map((step, idx) => (
                            <li key={step.title} className="flex items-start gap-4">
                                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xl font-bold">
                                    {idx + 1}.
                                </span>
                                <div>
                                    <h3 className="text-lg font-bold">{step.title}</h3>
                                    <p>{step.text}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-4 py-12 text-textColor">
                <h2 className="text-3xl sm:text-4xl font-extrabold mb-6">Was wir von dir wissen möchten</h2>
                <ul className="space-y-3">
                    {questions.map((question) => (
                        <li key={question} className="flex items-center gap-3 text-lg">
                            <img src={check} alt="" className="h-8 w-8 flex-shrink-0" />
                            {question}
                        </li>
                    ))}
                </ul>
                <p className="mt-6">Anlieferung und das Einräumen vor Ort klären wir gemeinsam.</p>
            </section>

            <section className="max-w-6xl mx-auto px-4 pb-12 text-textColor">
                <div className="flex flex-col items-start justify-between gap-4 rounded-2xl bg-secondaryLight p-6 sm:flex-row sm:items-center sm:p-8">
                    <h2 className="text-2xl sm:text-3xl font-extrabold">Bereit, uns deine Produkte zu zeigen?</h2>
                    <ActionLink to="#anbieten" variant="solid" event="cta-click" eventData={{ target: 'supplier-offer', location: 'suppliers-bottom' }}>Eigene Produkte anbieten {arrow}</ActionLink>
                </div>
            </section>

            {hash === "#anbieten" && (
                <section id="anbieten" className="max-w-4xl mx-auto px-4 pb-12 text-textColor scroll-mt-4">
                    <h2 className="text-2xl font-bold mb-2">Deine Produkte bei Yobsti? Lass uns kennenlernen.</h2>
                    <p className="mb-6">
                        Erzähle uns, was du anbietest und welche Yobsti-Standorte du beliefern könntest. Gemeinsam klären wir, ob dein Angebot zu den gewünschten Standorten passt.
                    </p>
                    <SupplierOfferForm />
                </section>
            )}
        </>
    );
}
